import {
	type SuiDataTableColumnType,
	SuiDataTableSortDirection,
	SuiDataTableColumnAlign,
} from '@developers/scoro-ui';
import type { TeamReportRowItem, TeamReportQuery } from '@/generated/graphql';
import type {
	TeamReportGroupTotalRow,
	GroupedRows,
	TeamReportGroupRow,
} from '@/features/TeamReport/types';
import {
	SORTABLE_COLUMNS,
	TABLE_COLUMN,
} from '@/features/TeamReport/constants';
import { isNumber } from 'fp-ts/number';
import { cloneDeep, memoize } from 'lodash';

type GroupTotals = { [key: string]: TeamReportGroupTotalRow };
export interface ColumnsConfig extends Omit<SuiDataTableColumnType, 'title'> {
	title: string | string[];
}
export const columnsConfig: Record<string, ColumnsConfig> = {
	user: {
		id: TABLE_COLUMN.User,
		title: 'user',
		titleLabel: '',
		width: '30rem',
		isInteractive: true,
		sort: {
			isActive: true,
			direction: SuiDataTableSortDirection.ASC,
		},
	},
	capacity: {
		id: TABLE_COLUMN.Capacity,
		title: 'team_report_capacity',
		titleLabel: 'team_report_capacity_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.ASC,
		},
	},
	timeOff: {
		id: TABLE_COLUMN.TimeOff,
		title: 'team_report_time_off',
		titleLabel: 'team_report_time_off_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	extraAvailability: {
		id: TABLE_COLUMN.ExtraAvailability,
		title: 'team_report_extra_availability',
		titleLabel: 'team_report_extra_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	availability: {
		id: TABLE_COLUMN.Availability,
		title: 'team_report_availability',
		titleLabel: 'team_report_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	bookings: {
		id: TABLE_COLUMN.Bookings,
		title: 'team_report_bookings',
		titleLabel: 'team_report_bookings_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	bookingsFixed: {
		id: TABLE_COLUMN.BookingsFixed,
		title: 'team_report_bookings_fixed',
		titleLabel: 'team_report_bookings_fixed_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	bookingsTentative: {
		id: TABLE_COLUMN.BookingsTentative,
		title: 'team_report_bookings_tentative',
		titleLabel: 'team_report_bookings_tentative_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	bookingsVsAvailability: {
		id: TABLE_COLUMN.BookingsVsAvailability,
		title: ['team_report_bookings', 'versus', 'team_report_availability'],
		titleLabel: 'team_report_bookings_vs_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	tasksPlanned: {
		id: TABLE_COLUMN.TasksPlanned,
		title: 'team_report_tasks_planned',
		titleLabel: 'team_report_tasks_planned_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntries: {
		id: TABLE_COLUMN.TimeEntries,
		title: 'team_report_time_entries',
		titleLabel: 'team_report_time_entries_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesScheduled: {
		id: TABLE_COLUMN.TimeEntriesScheduled,
		title: 'team_report_time_entries_scheduled',
		titleLabel: 'team_report_time_entries_scheduled_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesDone: {
		id: TABLE_COLUMN.TimeEntriesDone,
		title: 'team_report_time_entries_done',
		titleLabel: 'team_report_time_entries_done_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesVsAvailability: {
		id: TABLE_COLUMN.TimeEntriesVsAvailability,
		title: [
			'team_report_time_entries',
			'versus',
			'team_report_availability',
		],
		titleLabel: 'team_report_time_entries_vs_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesBillable: {
		id: TABLE_COLUMN.TimeEntriesBillable,
		title: 'team_report_time_entries_billable',
		titleLabel: 'team_report_time_entries_billable_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesBillableVsAvailability: {
		id: TABLE_COLUMN.TimeEntriesBillableVsAvailability,
		title: [
			'team_report_time_entries_billable',
			'versus',
			'team_report_availability',
		],
		titleLabel: 'team_report_billable_time_entries_vs_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesNonBillable: {
		id: TABLE_COLUMN.TimeEntriesNonBillable,
		title: 'team_report_time_entries_non_billable',
		titleLabel: 'team_report_time_entries_non_billable_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
	timeEntriesNonBillableVsAvailability: {
		id: TABLE_COLUMN.TimeEntriesNonBillableVsAvailability,
		title: [
			'team_report_time_entries_non_billable',
			'versus',
			'team_report_availability',
		],
		titleLabel:
			'team_report_non_billable_time_entries_vs_availability_tooltip',
		isInteractive: true,
		align: SuiDataTableColumnAlign.RIGHT,
		sort: {
			isActive: false,
			direction: SuiDataTableSortDirection.DESC,
		},
	},
};

export const getGroupTotals = (
	rows: TeamReportQuery['TeamReport']['rows'],
	name?: string
): GroupTotals => {
	const groupTotals: GroupTotals = {};

	rows.forEach((row) => {
		// allow enforcing a group name for custom calculations like summarising ungrouped rows
		const groupName = name ?? row.groupName;

		if (groupName) {
			if (!groupTotals[groupName]) {
				groupTotals[groupName] = {
					isTotalRow: true,
					amount: 0,
					groupName,
				} as TeamReportGroupTotalRow;
			}

			groupTotals[groupName].amount += 1;

			Object.entries(row).forEach(([key, value]) => {
				if (
					key === TABLE_COLUMN.User ||
					!SORTABLE_COLUMNS.includes(key) ||
					!groupName ||
					!value
				) {
					return;
				}

				const currentCellValue = value as TeamReportRowItem;

				if (!groupTotals[groupName][key]) {
					groupTotals[groupName][key] = {
						unit: currentCellValue.unit,
						value: 0,
					} as TeamReportRowItem;
				}

				const totalItem = groupTotals[groupName][
					key
				] as TeamReportRowItem;

				totalItem.value =
					(totalItem.value || 0) + (currentCellValue.value || 0);
			});
		}
	});

	return groupTotals;
};

export const getRowWithSumPercentage = (
	totalRows: number,
	row: TeamReportRowItem
): TeamReportRowItem => {
	if (row?.unit === '%' && row?.value && isNumber(row.value)) {
		const currentValue = row.value;
		const sumCorrectedPercentage = currentValue / totalRows;

		return {
			...row,
			value: sumCorrectedPercentage,
		};
	}

	return row;
};

export const getGroupTotalsWithSumPercentages = (
	groupTotals: GroupTotals
): GroupTotals => {
	const groupNames = Object.keys(groupTotals);
	const percentageUpdatedTotals: GroupTotals = cloneDeep(groupTotals);

	groupNames.forEach((groupName) => {
		Object.keys(percentageUpdatedTotals[groupName]).forEach((rowKey) => {
			if (percentageUpdatedTotals[groupName][rowKey]) {
				percentageUpdatedTotals[groupName][rowKey] =
					getRowWithSumPercentage(
						percentageUpdatedTotals[groupName].amount,
						percentageUpdatedTotals[groupName][rowKey]
					);
			}
		});
	});

	return percentageUpdatedTotals;
};

export const getRowsWithGroupsAndTotals = (
	rows: TeamReportQuery['TeamReport']['rows'],
	groupTotals: GroupTotals
): GroupedRows => {
	const groupNames = Object.keys(groupTotals);
	const ungroupedRows = [...rows] as GroupedRows;
	let previousGroupName: string | undefined = undefined;

	groupNames.forEach((key, index) => {
		const firstGroupItemIndex = ungroupedRows.findIndex(
			(row) => row.groupName === key
		);

		const itemsToAdd: (TeamReportGroupTotalRow | TeamReportGroupRow)[] = [];

		if (previousGroupName) {
			// We also add the totals for the previous group before adding the next group row
			itemsToAdd.push(groupTotals[previousGroupName]);
		}

		itemsToAdd.push({
			groupName: key,
			isGroupRow: true,
		});

		ungroupedRows.splice(firstGroupItemIndex, 0, ...itemsToAdd);

		if (groupNames.length === index + 1) {
			// Since it's the last group we're parsing we will also add it's totals
			ungroupedRows.push(groupTotals[key]);
		}

		previousGroupName = key;
	});

	return ungroupedRows;
};
const memoizedGroupTotals = memoize(getGroupTotals);
const memoizedGroupTotalsWithSumPercentages = memoize(
	getGroupTotalsWithSumPercentages
);
export const computeRowsWithTotal = (
	rows: TeamReportQuery['TeamReport']['rows']
) => {
	const totals = memoizedGroupTotals(rows, 'total-row');
	const adjustedTotalRow = memoizedGroupTotalsWithSumPercentages(totals);

	return [...rows, adjustedTotalRow['total-row']] as GroupedRows;
};
export const computeGroupedRows = (
	rows: TeamReportQuery['TeamReport']['rows']
) => {
	const groupTotals = memoizedGroupTotals(rows);

	const adjustedGroupTotals =
		memoizedGroupTotalsWithSumPercentages(groupTotals);

	return getRowsWithGroupsAndTotals(rows, adjustedGroupTotals);
};
